import styled from "@emotion/styled";
import { CategoryPageProps } from "./CategoryPageProps";
import { FeatureContainer, kafSystemWidth, systemWidth, useTheme } from "@kaltura/mediaspace-shared-styled";
import {
    ButtonClickAnalyticsType,
    CategorySection,
    ChannelHeaderType,
    ChannelLayoutMode,
    ChannelViewMode,
    EventPlatformEntry,
    SectionIndex,
    WebcastEntry,
} from "@kaltura/mediaspace-shared-types";
import { SyntheticEvent, useContext, useEffect, useMemo, useRef, useState } from "react";
import {
    getComponentFromPath,
    getTags,
    normalizeTags,
    sendButtonClickAnalytics,
    translate,
    useMediaQuery,
} from "@kaltura/mediaspace-shared-utils";
import Sessions from "./sessions/Sessions";
import PlaylistCarousels from "./playlist-carousels/PlaylistCarousels";
import AboutSection from "./about-section/AboutSection";
import ChannelHeader from "./channel-header/ChannelHeader";
import { Tab, Tabs, Typography } from "@kaltura/ds-react-components";
import { ErrorBoundary, TabPanel, tabProps } from "@kaltura/mediaspace-shared-ui";
import ActionsMenu from "./actions-menu/ActionsMenu";
import BoardMessages from "./board-messages/BoardMessages";
import { tabScrollButtonClasses } from "@mui/material";
import { ButtonAnalyticsProvider, useButtonAnalytics } from "@kaltura/mediaspace-shared-hooks";
import { ConfigContext } from "@kaltura/mediaspace-shared-data-kms-config";
import LeanChannelHeader from "./channel-header/lean-channel-header/LeanChannelHeader";
import { NotificationsManager } from "@kaltura/mediaspace-shared-channel-tv";
import sortBy from "lodash/sortBy";
import NoSections from "./no-sections/NoSections";
import { NoResults } from "@kaltura/mediaspace-shared-search";
import { convertToScheduledSessions } from "@kaltura/mediaspace-shared-event-platform-event-list";
import {
    appendFiltersToQueryString,
    FilterBar,
    filterSessions,
    FiltersValues,
    getFiltersValuesFromQueryString,
    removeAllQueryParams,
} from "@kaltura/mediaspace-shared-search-with-filter-tags";
import { MediaContainer } from "./media-section/MediaContainer";
import omitBy from "lodash/omitBy";
import { GalleryMediaContainer } from "./media-section/GalleryMediaContainer";
import { GalleryHeader } from "./channel-header/GalleryHeader";
import { ActionButtons } from "./action-buttons/ActionButtons";

const DEFAULT_EXTENDED_TAB_COMPONENTS_INDEX = 1000;

// region Styled components
const StyledCategoryContent = styled(
    "div",
    {
        shouldForwardProp(propName) {
            return !["isKaf", "isVerticalView", "hasMessages", "hasActions"].includes(propName.toString());
        },
    }
)<{ isKaf?: boolean; isVerticalView: boolean; hasMessages?: boolean; hasActions?: boolean }>(
    ({ theme, isKaf, isVerticalView, hasActions, hasMessages }) => ({
        "&": isKaf ? kafSystemWidth({ theme }) : systemWidth({ theme }),
        ...(isVerticalView && !hasMessages && !hasActions && { marginTop: theme.spacing(4) }),
    })
);

const StyledGappedContainer = styled(
    "div",
    {
        shouldForwardProp(propName) {
            return propName !== "hasActions" && propName !== "isVerticalView";
        },
    }
)<{ hasActions?: boolean; isVerticalView: boolean }>(({ theme, hasActions, isVerticalView }) => ({
    display: "flex",
    flexDirection: "column",
    rowGap: theme.spacing(2),
    marginTop: theme.spacing(4),
    marginBottom: isVerticalView && !hasActions ? theme.spacing(4) : 0,
    [theme.breakpoints.down(theme.breakpoints.values.md)]: {
        marginTop: theme.spacing(3),
        marginBottom: isVerticalView && !hasActions ? theme.spacing(3) : 0,
    },
}));

const StyledSearchContainer = styled("div")(({ theme }) => ({
    flexGrow: 1,
}));

const StyledActions = styled(
    "div",
    {
        shouldForwardProp(propName: PropertyKey): boolean {
            return propName !== "isVerticalView" && propName !== "showBorder" && propName !== "showSearchBar";
        },
    }
)<{ isVerticalView: boolean; showBorder: boolean; showSearchBar: boolean }>(
    ({ theme, isVerticalView, showBorder, showSearchBar }) => ({
        position: "relative",
        zIndex: 1,
        textAlign: "right",
        display: "flex",
        gap: theme.spacing(1),
        marginBottom: isVerticalView ? theme.spacing(4) : 0,
        marginTop: isVerticalView ? theme.spacing(4) : 0,
        [theme.breakpoints.down(theme.breakpoints.values.md)]: {
            marginBottom: isVerticalView ? theme.spacing(3) : 0,
            marginTop: isVerticalView ? theme.spacing(3) : 0,
        },
        justifyContent: showSearchBar ? "space-between" : "flex-end",
        ...(
            !isVerticalView && {
                marginLeft: "auto",
                paddingTop: theme.spacing(1.5),
            }
        ),
        ...(
            showBorder && {
                borderBottom: 1,
                borderColor: theme.kaltura.palette.tone4,
                borderBottomStyle: "solid",
            }
        ),
    })
);

const StyledSection = styled(
    "div",
    {
        shouldForwardProp(propName) {
            return propName !== "withMarginTop";
        },
    }
)<{ withMarginTop: boolean }>(({ theme, withMarginTop }) => ({
    marginTop: withMarginTop ? theme.spacing(7.5) : 0,
    [theme.breakpoints.down(theme.breakpoints.values.md)]: {
        marginTop: withMarginTop ? theme.spacing(5) : 0,
    },
}));

const StyledTabsAndActions = styled("div")({
    display: "flex",
});

const StyledTabsContainer = styled("div")({
    flex: "1 0 0",
    minWidth: 0,
});

const StyledTabs = styled(Tabs)({
    [`.${tabScrollButtonClasses.root}`]: {
        width: "auto",
    },
});

const StyledSectionTitle = styled(Typography)(({ theme }) => ({
    marginTop: theme.spacing(0),
    marginBottom: theme.spacing(3),
}));

const StyledTabbedSection = styled("div")(({ theme }) => ({
    marginTop: theme.spacing(3),
    [theme.breakpoints.down(theme.breakpoints.values.md)]: {
        marginTop: theme.spacing(2),
    },
}));
// endregion

// Empty array to be used as prop default values (because the default values should be constant reference)
const emptyArray: never[] = [];

/**
 * Channel Page
 */
export const CategoryPage = (props: CategoryPageProps) => {
    const {
        isChannel,
        entries = emptyArray,
        entriesTotalCount = 0,
        entriesPageSize = 15,
        showMediaTab = true,
        about = false,
        playlists = emptyArray,
        hasMorePlaylists,
        description,
        categoryId,
        media,
        actions,
        actionButtons = emptyArray,
        channelViewMode = ChannelViewMode.Grid,
        channelLayoutMode = ChannelLayoutMode.Tabs,
        defaultSection = SectionIndex.SESSIONS,
        showSpeakersForContentSectionEntries = false,
        boardMessages,
        name,
        channelHeaderType = ChannelHeaderType.Show_Banner,
        enableChannelTvNotifications = false,
        notificationTimeoutMS = 0,
        pageTopComponents,
        filterByTags = emptyArray,
        mediaFilterMenuProps,
        mediaFilters = emptyArray,
        showSearchInput,
        additionalSections = emptyArray,
    } = props;

    /**
     * search keyword
     */
    const [searchKeyword, setSearchKeyword] = useState<string>("");
    /**
     * tag filters values
     */
    const [tagFilterValues, setTagFilterValues] = useState<FiltersValues>();

    const [hasPlaylists, setHasPlaylists] = useState<boolean>(playlists.length > 0);
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down(theme.breakpoints.values.md));

    const readMoreSectionEl = useRef<HTMLDivElement>(null);

    const isVerticalView =
        channelLayoutMode === ChannelLayoutMode.Vertical || channelLayoutMode === ChannelLayoutMode.Fixed_Vertical;

    const isLeanHeader =
        channelHeaderType === ChannelHeaderType.Hide_Banner
        || channelHeaderType === ChannelHeaderType.Fixed_Hide_Banner;

    // content width is different in case of kaf
    const configContext = useContext(ConfigContext);
    const isKaf = configContext.application?.isKAF;
    const { dateFormats } = configContext.application;

    const onNoPlaylistsResults = () => {
        setHasPlaylists(false);
    };

    const memoizedPlaylistCarousels = useMemo(() => {
        return (
            <PlaylistCarousels
                hasMorePlaylists={!!hasMorePlaylists}
                hideFilters={isVerticalView || !showSearchInput}
                filterByTags={isVerticalView ? tagFilterValues : undefined}
                filterByKeyword={isVerticalView ? searchKeyword : undefined}
                onNoResults={isVerticalView ? onNoPlaylistsResults : undefined}
                content={playlists}
                categoryId={categoryId}
            />
        );
    }, [tagFilterValues, hasMorePlaylists, isVerticalView, searchKeyword, playlists, categoryId]);

    // filter out the media that shows on the channel header
    const entriesToShow = useMemo(
        () =>
            entries.filter((entry) => {
                return !media || entry.id !== media.entryId;
            }),
        [entries, media]
    );

    // extract "sessions" list
    const webcasts = useMemo(() => {
        if (entriesToShow.length > 0) {
            const entries = entriesToShow.filter((entry): entry is WebcastEntry => {
                return entry.schedulingData !== undefined && entry.schedulingData !== null;
            });
            const sessionEntries = convertToScheduledSessions(entries, dateFormats.longDateNoYear);
            return (searchKeyword || tagFilterValues) && isVerticalView
                ? filterSessions(sessionEntries, searchKeyword, tagFilterValues)
                : sessionEntries;
        }
        return [];
    }, [dateFormats.longDateNoYear, entriesToShow, isVerticalView, searchKeyword, tagFilterValues]);

    const sortedSessions = useMemo(
        () =>
            enableChannelTvNotifications ? sortBy(webcasts, (session) => session.schedulingData.start.timestamp) : [],
        [enableChannelTvNotifications, webcasts]
    );

    // extract "media" list
    const videos = useMemo(() => {
        const entries =
            entriesToShow.length > 0
                ? entriesToShow.filter((entry): entry is EventPlatformEntry => {
                      return entry.schedulingData === undefined || entry.schedulingData === null;
                  })
                : [];
        // filter entries only if in vertical view and searchKeyword exists
        if (entries.length > 0 && isVerticalView && (searchKeyword || tagFilterValues)) {
            return entries.filter((entry) => {
                const tags = normalizeTags(getTags(entry.tags));
                const searchString = `${entry.name}
                              ${entry.description || ""}
                              ${tags.join(",")}`.toLowerCase();
                // filter sessions by filters
                const tagFilters = Object
                    .entries(tagFilterValues || {})
                    .map(([param, values]) => [param, values.map((value) => value.toLowerCase())]);
                // satisfy every filter
                return (
                    tagFilters.every(
                        ([param, values]) =>
                            // tags contain some filter values
                            param !== "date" && tags.filter(Set.prototype.has, new Set(values)).length > 0
                    ) && searchString.includes(searchKeyword.toLowerCase())
                );
            });
        }
        return entries;
    }, [entriesToShow, isVerticalView, searchKeyword, tagFilterValues]);

    const sections = useMemo(() => {
        const sections: CategorySection[] = [];

        const addNoMediaTabWithActionButtons = () => {
            if (actionButtons.length > 0) {
                /*
                 * Currently, the only possible action button is the "add to channel/gallery" button,
                 * and it's not expected for other action buttons to be added in the observable future.
                 * If other action buttons will be added in the future, the page will get re-design anyway.
                 *
                 * So, if there's no content for the "media" tab, but the user is allowed to add new content,
                 * then show the tab with a "no content yet" screen and the action button.
                 */
                sections.push({
                    id: "media",
                    name: translate("Media"),
                    content: undefined,
                    component: NoSections,
                    index: SectionIndex.MEDIA,
                    additionalParams: {
                        actionButtonsDescription:
                            actionButtons.length > 0 ? translate("Get started by adding new content now.") : undefined,
                        actionButtons,
                        analyticsValue: "Media",
                    },
                });
            }
        };

        if (isChannel) {
            if (webcasts.length > 0) {
                sections.push({
                    id: "sessions",
                    name: translate("Sessions"),
                    content: webcasts,
                    component: Sessions,
                    index: SectionIndex.SESSIONS,
                    additionalParams: {
                        hideFilters: isVerticalView,
                        analyticsValue: "Sessions",
                        filters: filterByTags,
                        showSearchInput: showSearchInput,
                    },
                });
            }
            if (showMediaTab) {
                if (videos.length > 0) {
                    sections.push({
                        id: "media",
                        name: translate("Media"),
                        content: videos,
                        component: MediaContainer,
                        index: SectionIndex.MEDIA,
                        additionalParams: {
                            isGridView: channelViewMode === ChannelViewMode.Grid,
                            isVerticalView,
                            showSpeakersForContentSectionEntries,
                            filters: mediaFilters,
                            filterMenuProps: mediaFilterMenuProps,
                            showSearchInput: showSearchInput,
                            analyticsValue: "Media",
                        },
                    });
                }
                else {
                    addNoMediaTabWithActionButtons();
                }
            }
        }
        else if (showMediaTab) {
            if (entries.length > 0) {
                sections.push({
                    id: "media",
                    name: translate("Media"),
                    content: {
                        entries,
                        totalCount: entriesTotalCount,
                    },
                    component: GalleryMediaContainer,
                    index: SectionIndex.MEDIA,
                    additionalParams: {
                        pageSize: entriesPageSize,
                        isGridView: channelViewMode === ChannelViewMode.Grid,
                        showSpeakersForContentSectionEntries,
                        filters: mediaFilters,
                        filterMenuProps: mediaFilterMenuProps,
                        analyticsValue: "Media",
                    },
                });
            }
            else {
                addNoMediaTabWithActionButtons();
            }
        }
        if (playlists.length > 0) {
            sections.push({
                id: "playlists",
                name: translate("Playlists"),
                content: playlists,
                component: () => memoizedPlaylistCarousels,
                index: SectionIndex.PLAYLISTS,
                additionalParams: {
                    analyticsValue: "Playlist",
                },
            });
        }
        if (about && description) {
            sections.push({
                id: "learn-more",
                name: translate("Learn More"),
                content: description,
                component: AboutSection,
                index: SectionIndex.LEARN_MORE,
                additionalParams: {
                    analyticsValue: "Learn More",
                },
            });
        }

        additionalSections.forEach((section) => {
            sections.push({
                ...section,
                name: section.name,
                index: section.index || DEFAULT_EXTENDED_TAB_COMPONENTS_INDEX,
            });
        });

        //sort sections by index attribute
        sections.sort((a, b) => {
            return a.index - b.index;
        });

        // support no sections
        if (sections.length === 0) {
            return [];
        }

        // set defaultSection as first item in sections array, if exists
        const firstSection =
            [...sections, ...additionalSections].find((section) => Number(section.index) === Number(defaultSection))
            || sections[0];
        const filteredSections = sections.filter((section) => section !== firstSection);
        return [firstSection, ...filteredSections];
    }, [
        isChannel,
        channelViewMode,
        showSpeakersForContentSectionEntries,
        entries,
        entriesPageSize,
        entriesTotalCount,
        showMediaTab,
        webcasts,
        videos,
        playlists,
        about,
        description,
        additionalSections,
        isVerticalView,
        filterByTags,
        showSearchInput,
        mediaFilters,
        mediaFilterMenuProps,
        memoizedPlaylistCarousels,
        defaultSection,
        actionButtons,
    ]);

    /*
     * The code above already moves the default tab to be the first one.
     * So here we only need to select the first tab.
     */
    const [activeSection, setActiveSection] = useState(sections[0]?.id);

    const handleActiveTab = (event: SyntheticEvent, newValue: string) => {
        setActiveSection(newValue);
    };

    const onReadMore = (() => {
        if (!about) {
            return;
        }
        return () => {
            if (isVerticalView) {
                readMoreSectionEl.current?.scrollIntoView({
                    behavior: "smooth",
                    block: "start",
                });
            }
            else {
                const learnMoreSection = sections.find((section) => Number(section.index) === SectionIndex.LEARN_MORE);
                if (learnMoreSection) {
                    setActiveSection(learnMoreSection.id);
                }
            }
        };
    })();

    const sendButtonAnalytics = useButtonAnalytics();

    const handleSearchTermChange = (value: string) => {
        // KeywordSearch rendered only in vertical view
        if (!isVerticalView) {
            return;
        }
        // reset playlists hasResults to its initial value
        setHasPlaylists(playlists.length > 0);
        setSearchKeyword(value);
        sendButtonClickAnalytics("Search", "Channel - Vertical view", ButtonClickAnalyticsType.SEARCH);
    };

    /**
     * This effect runs when the `filterByTags` prop changes, this prop hold the list of available tag filter values.
     * It retrieves selected filter values from the query string and updates the tag filter state (to represent selected filters).
     */
    useEffect(() => {
        // Retrieve filter values from the query string based on the provided filter tags
        const filterValuesFromQueryString = getFiltersValuesFromQueryString(filterByTags);

        // Iterate over the retrieved filter values and update the tag filter state
        Object
            .entries(filterValuesFromQueryString)
            .forEach(([key, value]) => {
                handleTagFilterChange(key, value, false); // Update state without appending to the query string
            });
    }, [filterByTags]);

    /**
     * Handles changes to tag filters.
     * Updates the state with new filter values and optionally appends them to the query string.
     * Sends analytics for the filter change.
     *
     * @param {string} param - The filter parameter name.
     * @param {string[]} values - The filter values.
     * @param {boolean} [appendToQueryString=true] - Whether to append the filter values to the query string.
     */
    const handleTagFilterChange = (param: string, values: string[], appendToQueryString = true) => {
        // reset playlists hasResults to its initial value
        setHasPlaylists(playlists.length > 0);
        // Update the tag filter values state, removing any empty filters
        setTagFilterValues((filterValues) => {
            // `omitBy` removes properties with empty arrays as values
            const newValues = omitBy({ ...filterValues, [param]: values }, (elem) => elem.length === 0);
            // Return the new filter values if there are any, otherwise return undefined
            return Object.keys(newValues).length > 0 ? newValues : undefined;
        });

        // Optionally append the filter values to the query string
        if (appendToQueryString) {
            appendFiltersToQueryString(param, values);
        }

        sendButtonClickAnalytics("Filter by tag", "Channel - Vertical view", ButtonClickAnalyticsType.SEARCH);
    };

    const handleClearBubbles = () => {
        // clear all filters
        setTagFilterValues(undefined);
        removeAllQueryParams(filterByTags);
    };

    const hasMessages =
        (pageTopComponents && pageTopComponents.length > 0) || (boardMessages && boardMessages.length > 0);

    const hasActions = showSearchInput || (filterByTags && filterByTags.length > 0) || (actions && actions.length > 0);
    const isInSearchContext = tagFilterValues || searchKeyword;
    const styledActions = (
        <StyledActions
            isVerticalView={isVerticalView}
            showBorder={sections.length > 0 && !isVerticalView}
            showSearchBar={isVerticalView && sections.length > 0}
        >
            {isVerticalView && (sections.length > 0 || isInSearchContext) && (
                // setSearchKeyword
                <StyledSearchContainer>
                    <FilterBar
                        onInputSubmit={handleSearchTermChange}
                        onClearBubbles={handleClearBubbles}
                        onFilterChange={handleTagFilterChange}
                        showInputClearButton={false}
                        filters={filterByTags}
                        values={tagFilterValues}
                        withSearchInput={showSearchInput}
                    />
                </StyledSearchContainer>
            )}
            <ActionButtons actionButtons={actionButtons} />
            {actions && actions.length > 0 && <ActionsMenu isChannel={isChannel} actions={actions} />}
        </StyledActions>
    );

    return (
        <FeatureContainer className={"kms-ds-channel-page"} isKaf={isKaf}>
            {isChannel && isLeanHeader && <LeanChannelHeader {...props} />}
            {isChannel && !isLeanHeader && <ChannelHeader {...props} onReadMore={onReadMore} />}
            {!isChannel && <GalleryHeader {...props} onReadMore={onReadMore} />}

            <StyledCategoryContent
                isKaf={isKaf}
                isVerticalView={isVerticalView}
                hasMessages={hasMessages}
                hasActions={hasActions}
                className={"kms-ds-channel-page-body"}
            >
                {hasMessages && (
                    <StyledGappedContainer isVerticalView={isVerticalView} hasActions={hasActions}>
                        {pageTopComponents
                            && pageTopComponents?.map(({ name, component, props }, ind) => {
                                const Component = getComponentFromPath(component);
                                return (
                                    <ErrorBoundary key={name} fallback={<span></span>}>
                                        <span className={"kms-ds-channel-page-top-component"} key={ind}>
                                            <Component {...props} />
                                        </span>
                                    </ErrorBoundary>
                                );
                            })}
                        {boardMessages && boardMessages.length > 0 && (
                            <BoardMessages boardMessages={boardMessages} categoryName={name} categoryId={categoryId} />
                        )}
                    </StyledGappedContainer>
                )}
                {isVerticalView && (showSearchInput || (actions && actions.length > 0)) && styledActions}

                {!isVerticalView && (
                    <>
                        {(styledActions || sections.length > 0) && (
                            <StyledTabsAndActions>
                                {sections.length > 0 && (
                                    <StyledTabsContainer>
                                        <StyledTabs
                                            variant={isSmallScreen ? "scrollable" : "standard"}
                                            scrollButtons={isSmallScreen}
                                            allowScrollButtonsMobile={isSmallScreen}
                                            value={activeSection}
                                            onChange={handleActiveTab}
                                        >
                                            {sections.map((section) => {
                                                return (
                                                    <Tab
                                                        {...tabProps(section.id)}
                                                        label={section.name}
                                                        key={`tab-${section.id}`}
                                                        onClick={() => {
                                                            sendButtonAnalytics(
                                                                `${isChannel ? "Channel" : "Gallery"} Tab - ${section.additionalParams?.analyticsValue}`,
                                                                ButtonClickAnalyticsType.BROWSE
                                                            );
                                                        }}
                                                    />
                                                );
                                            })}
                                        </StyledTabs>
                                    </StyledTabsContainer>
                                )}
                                {styledActions}
                            </StyledTabsAndActions>
                        )}
                        {sections.length > 0 ? (
                            sections.map((section) => {
                                return (
                                    <TabPanel
                                        currentTab={activeSection}
                                        name={section.id}
                                        key={`section-${section.id}`}
                                    >
                                        <StyledTabbedSection className={"kms-ds-channel-page-section-tabs"}>
                                            <ButtonAnalyticsProvider
                                                location={`Tabs - ${section.additionalParams?.analyticsValue}`}
                                            >
                                                <section.component
                                                    isComponentInView={activeSection === section.name} // used originally for PS components
                                                    content={section.content}
                                                    categoryId={categoryId}
                                                    {...section.additionalParams}
                                                />
                                            </ButtonAnalyticsProvider>
                                        </StyledTabbedSection>
                                    </TabPanel>
                                );
                            })
                        ) : (
                            <NoSections />
                        )}
                    </>
                )}

                {isVerticalView && (
                    <>
                        {searchKeyword
                            && isVerticalView
                            && videos.length === 0
                            && webcasts.length === 0
                            && !hasPlaylists && <NoResults screenMode={"nomatches"} />}

                        {sections.length > 0 ? (
                            sections.map((section, index) => {
                                return (
                                    <StyledSection
                                        withMarginTop={index !== 0}
                                        id={`section-${section.index}`}
                                        key={`section-${section.index}`}
                                        className={`kms-ds-channel-page-vertical-section-wrapper`}
                                        ref={
                                            section.component.displayName === "AboutSection" ? readMoreSectionEl : null
                                        }
                                    >
                                        {section.index !== SectionIndex.PLAYLISTS ? (
                                            <StyledSectionTitle
                                                component={"h2"}
                                                variant={"h3"}
                                                className={`kms-ds-channel-page-vertical-section-title`}
                                            >
                                                {section.name}
                                            </StyledSectionTitle>
                                        ) : null}
                                        <ButtonAnalyticsProvider
                                            location={`Tabs - ${section.additionalParams?.analyticsValue}`}
                                        >
                                            <section.component
                                                isComponentInView={true} // used originally for PS components
                                                content={section.content}
                                                categoryId={categoryId}
                                                {...section.additionalParams}
                                            />
                                        </ButtonAnalyticsProvider>
                                    </StyledSection>
                                );
                            })
                        ) : (
                            <NoSections />
                        )}
                    </>
                )}
            </StyledCategoryContent>

            {enableChannelTvNotifications && (
                <NotificationsManager
                    sessions={sortedSessions as WebcastEntry[]}
                    notificationTimeoutMS={notificationTimeoutMS}
                    channelId={categoryId}
                ></NotificationsManager>
            )}
        </FeatureContainer>
    );
};

export default CategoryPage;
